import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Chip,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  HelpOutline,
  Inventory2,
  ShowChart,
  EmojiEvents,
  Schedule
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rotationAPI } from '../services/api';
import { 
  SectorRotationSkeleton, 
  MoneyFlowSection, 
  EntryTimingPanel,
  OutsideRotationWatchlist
} from '../components/sector-rotation';

const SectorRotation = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [moneyFlow, setMoneyFlow] = useState(null);
  const [showOnlySafeEntry, setShowOnlySafeEntry] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch summary
      const response = await rotationAPI.getSummary();
      const result = response.data;
      
      if (result.success) {
        setData(result);
        
        // Fetch alerts
        try {
          const alertsResponse = await rotationAPI.getAlerts();
          const alertsResult = alertsResponse.data;
          if (alertsResult.success) {
            setAlerts(alertsResult.alerts || []);
          }
        } catch (alertErr) {
          console.warn('Failed to fetch alerts:', alertErr);
        }
        
        // Fetch money flow (last 3 days for freshness)
        try {
          const flowResponse = await rotationAPI.getMoneyFlow(3);
          const flowResult = flowResponse.data;
          if (flowResult.success) {
            setMoneyFlow(flowResult);
          }
        } catch (flowErr) {
          console.warn('Failed to fetch money flow:', flowErr);
        }
      } else {
        setError(result.message || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error fetching rotation data:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toFixed(decimals);
  };

  const formatPercent = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    const sign = num >= 0 ? '+' : '';
    return `${sign}${Number(num).toFixed(decimals)}%`;
  };

  if (loading) {
    return <SectorRotationSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, maxWidth: '100%', px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data || !data.success) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, maxWidth: '100%', px: 2 }}>
        <Alert severity="warning">
          {data?.message || 'No data available'}
        </Alert>
      </Container>
    );
  }

  const { sectors, stocks, holdings, alerts: alertsSummary, data_date, freshness } = data;
  
  // Use money flow data from API (with score changes)
  const exitFlows = moneyFlow?.exits || [];
  const entryFlows = moneyFlow?.entries || [];
  
  // Get top 3 exits and entries by absolute score change
  const topExits = exitFlows
    .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change))
    .slice(0, 3);
  
  const topEntries = entryFlows
    .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change))
    .slice(0, 3);
  
  // Balance display
  const maxDisplay = Math.min(topExits.length, topEntries.length);
  const balancedExits = topExits.slice(0, maxDisplay);
  const balancedEntries = topEntries.slice(0, maxDisplay);
  
  // Calculate setup types from top_picks
  const setupCounts = stocks?.top_picks?.reduce((acc, stock) => {
    const setup = stock.setup_type || 'NONE';
    acc[setup] = (acc[setup] || 0) + 1;
    return acc;
  }, {}) || {};
  
  const topSetups = {
    momentum: setupCounts['MOMENTUM'] || 0,
    breakout: setupCounts['BREAKOUT'] || 0,
    pullback: setupCounts['PULLBACK'] || 0,
    none: setupCounts['NONE'] || 0
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 2 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%', px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box 
              sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(118, 75, 162, 0.25)',
              }}
            >
              <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                {t('dashboard.sectorRotation.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('dashboard.sectorRotation.subtitle')}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" gap={1.5} alignItems="center">
            <Chip 
              label={`${t('dashboard.sectorRotation.dataDate')}: ${data_date}`} 
              color="primary"
              size="small"
            />
            {freshness && (
              <Chip 
                label={
                  freshness.can_trade 
                    ? t('dashboard.sectorRotation.marketOpen', 'Thị trường đang mở')
                    : `⚠️ ${t('dashboard.sectorRotation.daysOld', { days: freshness.days_old }, `${freshness.days_old} ngày trước`)} - ${freshness.warning}`
                }
                size="small"
                icon={freshness.can_trade ? <CheckCircle /> : undefined}
                sx={freshness.can_trade ? {
                  bgcolor: '#10b981',
                  color: '#ffffff',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#ffffff' },
                  '& .MuiChip-label': { px: 1.5 }
                } : { 
                  bgcolor: '#f59e0b',
                  color: '#ffffff',
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Active Sectors Chips */}
        {sectors?.active > 0 && (
          <Paper 
            elevation={0}
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: '#64748b', 
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mb: 1.5
              }}
            >
              {t('dashboard.sectorRotation.activeSectors')} ({sectors.active})
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {sectors.top_5?.filter((s) => s.rank <= sectors.active).map((sector, idx) => {
                const scoreColors = [
                  { bg: '#dcfce7', border: '#10b981', text: '#065f46' }, // Top 1 - green
                  { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' }, // Top 2 - blue
                  { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' }, // Top 3 - indigo
                  { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }, // Top 4 - amber
                  { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' }, // Top 5 - red
                ];
                const colorStyle = scoreColors[idx] || scoreColors[4];
                
                return (
                  <Box
                    key={sector.sector_code}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: colorStyle.bg,
                      border: `1px solid ${colorStyle.border}`,
                      borderRadius: 1.5,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <TrendingUp sx={{ fontSize: 16, color: colorStyle.border }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        color: colorStyle.text
                      }}
                    >
                      {sector.sector_name?.vi || sector.sector_code}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: colorStyle.border,
                        bgcolor: 'white',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.75
                      }}
                    >
                      {formatNumber(sector.score, 1)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Money Flow Visualization */}
        <MoneyFlowSection 
          moneyFlow={moneyFlow}
          balancedExits={balancedExits}
          balancedEntries={balancedEntries}
          formatNumber={formatNumber}
        />

        {/* Entry Timing System (Phase 2 - Tier C) */}
        <Box sx={{ mb: 2 }}>
          <EntryTimingPanel />
        </Box>

        {/* Outside Rotation Watchlist (rotation_edge P5/P6) */}
        <Box sx={{ mb: 2, width: '100%' }}>
          <OutsideRotationWatchlist />
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, width: '100%' }}>
          {/* Card 1: Active Sectors */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1,
              p: 2.5, 
              borderRadius: 2,
              borderLeft: '4px solid #667eea',
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
                borderColor: '#667eea'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dashboard.sectorRotation.activeSectors')}
              </Typography>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp sx={{ fontSize: 18, color: '#667eea' }} />
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography variant="h2" fontWeight={700} sx={{ color: '#1e293b', lineHeight: 1, fontSize: '2rem' }}>
                {sectors?.active || 0}
              </Typography>
              <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                / {sectors?.total || 0}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>
                {t('dashboard.sectorRotation.outOfSectors', { total: sectors?.total || 11 })}
              </Typography>
              <Typography variant="caption" sx={{ color: '#667eea', fontSize: '0.75rem', fontWeight: 700 }}>
                {Math.round((sectors?.active / sectors?.total) * 100 || 0)}%
              </Typography>
            </Box>
            <Box display="flex" gap={1.5} mb={1} flexWrap="wrap">
              {sectors?.top_5?.slice(0, 2).map((sector, idx) => (
                <Typography 
                  key={sector.sector_code}
                  variant="caption"
                  sx={{ 
                    color: idx === 0 ? '#667eea' : '#64748b', 
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                >
                  {sector.sector_name?.vi || sector.sector_code} <Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(sector.score, 1)}</Box>
                </Typography>
              ))}
              {sectors?.active > 2 && (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600 }}>
                  +{sectors.active - 2}
                </Typography>
              )}
            </Box>
            <Box>
              <LinearProgress 
                variant="determinate" 
                value={(sectors?.active / sectors?.total) * 100 || 0}
                sx={{ 
                  height: 4, 
                  borderRadius: 2,
                  bgcolor: '#f1f5f9',
                  mb: 0.5,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#667eea',
                    borderRadius: 2
                  }
                }}
              />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                  {t('dashboard.sectorRotation.activeCoverage')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#667eea', fontSize: '0.65rem', fontWeight: 600 }}>
                  {sectors?.active || 0}/{sectors?.total || 11}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Card 2: Stock Candidates */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1,
              p: 2.5, 
              borderRadius: 2,
              borderLeft: '4px solid #ec4899',
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
                borderColor: '#ec4899'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dashboard.sectorRotation.stockCandidates')}
              </Typography>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp sx={{ fontSize: 18, color: '#ec4899' }} />
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography variant="h2" fontWeight={700} sx={{ color: '#1e293b', lineHeight: 1, fontSize: '2rem' }}>
                {stocks?.candidates || 0}
              </Typography>
              {stocks?.candidates > 0 && (
                <Typography 
                  variant="caption"
                  sx={{ 
                    ml: 'auto',
                    color: '#ec4899', 
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}
                >
                  {t('dashboard.sectorRotation.ready')}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500, display: 'block', mb: 1 }}>
              {t('dashboard.sectorRotation.fromActiveSectors', { count: sectors?.active || 0 })}
            </Typography>
            <Box>
              <Box display="flex" gap={1.5} mb={1}>
                <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.7rem', fontWeight: 700 }}>
                  {topSetups.pullback} PULLBACK
                </Typography>
                <Typography variant="caption" sx={{ color: '#ec4899', fontSize: '0.7rem', fontWeight: 700 }}>
                  {topSetups.momentum} MOMENTUM
                </Typography>
                {topSetups.none > 0 && (
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 700 }}>
                    {topSetups.none} NONE
                  </Typography>
                )}
              </Box>
              {stocks?.candidates > 0 ? (
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    {topSetups.pullback > 0 && (
                      <Box sx={{ 
                        flex: topSetups.pullback, 
                        height: 4, 
                        bgcolor: '#3b82f6', 
                        borderRadius: 2 
                      }} />
                    )}
                    {topSetups.momentum > 0 && (
                      <Box sx={{ 
                        flex: topSetups.momentum, 
                        height: 4, 
                        bgcolor: '#ec4899', 
                        borderRadius: 2 
                      }} />
                    )}
                    {topSetups.none > 0 && (
                      <Box sx={{ 
                        flex: topSetups.none, 
                        height: 4, 
                        bgcolor: '#cbd5e1', 
                        borderRadius: 2 
                      }} />
                    )}
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                      {t('dashboard.sectorRotation.setupTypes')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1e293b', fontSize: '0.65rem', fontWeight: 600 }}>
                      {stocks.candidates}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={0}
                    sx={{ 
                      height: 4, 
                      borderRadius: 2,
                      bgcolor: '#f1f5f9',
                      mb: 0.5,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#cbd5e1',
                        borderRadius: 2
                      }
                    }}
                  />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                      {t('dashboard.sectorRotation.waitingForSignals')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600 }}>
                      0
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Card 3: Active Holdings */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1,
              p: 2.5, 
              borderRadius: 2,
              borderLeft: holdings?.active?.avg_pnl_pct >= 0 ? '4px solid #10b981' : '4px solid #ef4444',
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
                borderColor: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dashboard.sectorRotation.activeHoldings')}
              </Typography>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: 1.5, 
                bgcolor: holdings?.active?.avg_pnl_pct >= 0 ? '#d1fae5' : '#fee2e2', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                {holdings?.active?.avg_pnl_pct >= 0 ? 
                  <TrendingUp sx={{ fontSize: 18, color: '#10b981' }} /> : 
                  <TrendingDown sx={{ fontSize: 18, color: '#ef4444' }} />
                }
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography 
                variant="h2" 
                fontWeight={700} 
                sx={{ 
                  color: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444', 
                  lineHeight: 1,
                  fontSize: '2rem'
                }}
              >
                {formatPercent(holdings?.active?.avg_pnl_pct || 0)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>
                {holdings?.active?.total || 0} {t('dashboard.sectorRotation.positions')} • {t('dashboard.sectorRotation.avgDays', { days: holdings?.active?.avg_days || 0 })}
              </Typography>
              <Chip 
                label={`${holdings?.active?.winning || 0}W / ${(holdings?.active?.total || 0) - (holdings?.active?.winning || 0)}L`}
                size="small"
                sx={{ 
                  bgcolor: holdings?.active?.avg_pnl_pct >= 0 ? '#d1fae5' : '#fee2e2', 
                  color: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444', 
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  height: 20
                }}
              />
            </Box>
            <Box>
              <Box display="flex" gap={1.5} mb={1}>
                <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 700 }}>
                  {holdings?.active?.winning || 0} {t('dashboard.sectorRotation.winning')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem', fontWeight: 700 }}>
                  {(holdings?.active?.total || 0) - (holdings?.active?.winning || 0)} {t('dashboard.sectorRotation.losing')}
                </Typography>
              </Box>
              <Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(Math.abs(holdings?.active?.avg_pnl_pct || 0), 100)}
                  sx={{ 
                    height: 4, 
                    borderRadius: 2,
                    bgcolor: '#f1f5f9',
                    mb: 0.5,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444',
                      borderRadius: 2
                    }
                  }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                    {t('dashboard.sectorRotation.performance')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444', fontSize: '0.65rem', fontWeight: 600 }}>
                    {formatPercent(holdings?.active?.avg_pnl_pct || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Card 4: Alerts */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1,
              p: 2.5, 
              borderRadius: 2,
              borderLeft: alertsSummary?.critical > 0 ? '4px solid #ef4444' : '4px solid #10b981',
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
                borderColor: alertsSummary?.critical > 0 ? '#ef4444' : '#10b981'
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dashboard.sectorRotation.alerts')}
              </Typography>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: 1.5, 
                bgcolor: alertsSummary?.critical > 0 ? '#fee2e2' : '#d1fae5', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                {alertsSummary?.critical > 0 ? 
                  <Warning sx={{ fontSize: 18, color: '#ef4444' }} /> : 
                  <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />
                }
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              {alertsSummary?.total > 0 ? (
                <>
                  <Typography variant="h2" fontWeight={700} sx={{ color: '#ef4444', lineHeight: 1, fontSize: '2rem' }}>
                    {alertsSummary?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600, opacity: 0.8, fontSize: '0.875rem' }}>
                    {t('dashboard.sectorRotation.alerts').toLowerCase()}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h2" fontWeight={700} sx={{ color: '#10b981', lineHeight: 1, fontSize: '2rem' }}>
                    ✓
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.875rem' }}>
                    {t('dashboard.sectorRotation.allClear')}
                  </Typography>
                </>
              )}
            </Box>
            
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>
                {alertsSummary?.total > 0 ? t('dashboard.sectorRotation.requireAttention') : t('dashboard.sectorRotation.systemStatus')}
              </Typography>
              {alertsSummary?.total > 0 && (
                <Typography 
                  variant="caption"
                  sx={{ 
                    color: alertsSummary?.critical > 0 ? '#dc2626' : '#d97706', 
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}
                >
                  {t('dashboard.sectorRotation.review')}
                </Typography>
              )}
            </Box>
            
            {alertsSummary?.total > 0 ? (
              <Box>
                <Box display="flex" gap={1.5} flexWrap="wrap" mb={1}>
                  {(alertsSummary?.critical > 0 || alertsSummary?.high > 0 || alertsSummary?.medium > 0) ? (
                    <>
                      {alertsSummary?.critical > 0 && (
                        <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.7rem' }}>
                          {alertsSummary.critical} {t('dashboard.sectorRotation.critical')}
                        </Typography>
                      )}
                      {alertsSummary?.high > 0 && (
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, fontSize: '0.7rem' }}>
                          {alertsSummary.high} {t('dashboard.sectorRotation.high')}
                        </Typography>
                      )}
                      {alertsSummary?.medium > 0 && (
                        <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.7rem' }}>
                          {alertsSummary.medium} {t('dashboard.sectorRotation.medium')}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, fontSize: '0.7rem' }}>
                      {alertsSummary.total} {t('dashboard.sectorRotation.pendingReview')}
                    </Typography>
                  )}
                </Box>
                {(alertsSummary?.critical > 0 || alertsSummary?.high > 0 || alertsSummary?.medium > 0) ? (
                  <Box display="flex" gap={0.5} mb={0.5}>
                    {alertsSummary?.critical > 0 && (
                      <Box sx={{ 
                        flex: alertsSummary.critical, 
                        height: 4, 
                        bgcolor: '#ef4444', 
                        borderRadius: 2 
                      }} />
                    )}
                    {alertsSummary?.high > 0 && (
                      <Box sx={{ 
                        flex: alertsSummary.high, 
                        height: 4, 
                        bgcolor: '#f59e0b', 
                        borderRadius: 2 
                      }} />
                    )}
                    {alertsSummary?.medium > 0 && (
                      <Box sx={{ 
                        flex: alertsSummary.medium, 
                        height: 4, 
                        bgcolor: '#3b82f6', 
                        borderRadius: 2 
                      }} />
                    )}
                  </Box>
                ) : (
                  <Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={100}
                      sx={{ 
                        height: 4, 
                        borderRadius: 2,
                        bgcolor: '#fef3c7',
                        mb: 0.5,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#f59e0b',
                          borderRadius: 2
                        }
                      }}
                    />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                        {t('dashboard.sectorRotation.needsReview')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#d97706', fontSize: '0.65rem', fontWeight: 600 }}>
                        {alertsSummary.total}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <LinearProgress 
                  variant="determinate" 
                  value={100}
                  sx={{ 
                    height: 4, 
                    borderRadius: 2,
                    bgcolor: '#f1f5f9',
                    mb: 0.5,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#10b981',
                      borderRadius: 2
                    }
                  }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                    {t('dashboard.sectorRotation.systemStatus')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.65rem', fontWeight: 600 }}>
                    ✓
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48
              },
              '& .Mui-selected': {
                color: '#667eea'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3
              }
            }}
          >
            <Tab label={t('dashboard.sectorRotation.tabs.topSectors')} />
            <Tab label={t('dashboard.sectorRotation.tabs.stockCandidates')} />
            <Tab label={t('dashboard.sectorRotation.tabs.holdings')} />
            <Tab label={`${t('dashboard.sectorRotation.tabs.alerts')} (${alerts.length})`} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 600, fontSize: '0.875rem', color: '#64748b', py: 1.5 } }}>
                    <TableCell>{t('dashboard.sectorRotation.table.rank')}</TableCell>
                    <TableCell>{t('dashboard.sectorRotation.table.sector')}</TableCell>
                    <TableCell align="right">{t('dashboard.sectorRotation.table.score')}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {t('dashboard.sectorRotation.table.rsMom20')}
                        <Tooltip title={t('dashboard.sectorRotation.table.rsMom20Tooltip')} arrow placement="top">
                          <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {t('dashboard.sectorRotation.table.turnoverSurge')}
                        <Tooltip title={t('dashboard.sectorRotation.table.turnoverSurgeTooltip')} arrow placement="top">
                          <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {t('dashboard.sectorRotation.table.breadthMA20')}
                        <Tooltip title={t('dashboard.sectorRotation.table.breadthMA20Tooltip')} arrow placement="top">
                          <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {t('dashboard.sectorRotation.table.breadthMA50')}
                        <Tooltip title={t('dashboard.sectorRotation.table.breadthMA50Tooltip')} arrow placement="top">
                          <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectors?.top_5?.map((sector) => (
                    <TableRow key={sector.sector_code}>
                      <TableCell>{sector.rank}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#1e293b' }}>
                          {sector.sector_name?.vi || sector.sector_code}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.75rem' }}>
                          {sector.sector_name?.en || sector.sector_code}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(sector.score, 1)} 
                          color="primary" 
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          {sector.rs_mom20 >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                          <Typography 
                            variant="body2" 
                            color={sector.rs_mom20 >= 0 ? 'success.main' : 'error.main'}
                            fontWeight={500}
                            ml={0.5}
                          >
                            {formatPercent(sector.rs_mom20 * 100)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2"
                          color={sector.turnover_surge >= 1 ? 'success.main' : 'text.secondary'}
                        >
                          {formatNumber(sector.turnover_surge, 2)}x
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2">
                            {formatNumber(sector.breadth_ma20, 1)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={sector.breadth_ma20} 
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2">
                            {formatNumber(sector.breadth_ma50, 1)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={sector.breadth_ma50} 
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 1 && (
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            {(() => {
              const topPicks = stocks?.top_picks || [];
              const displayedCandidates = showOnlySafeEntry
                ? topPicks.filter((s) => s.passes_safe_entry === true)
                : [...topPicks].sort((a, b) => (b.passes_safe_entry === true ? 1 : 0) - (a.passes_safe_entry === true ? 1 : 0));
              return (
                <>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.sectorRotation.table.safeEntry')}: {showOnlySafeEntry ? t('dashboard.sectorRotation.table.filterOnlySafe') : t('dashboard.sectorRotation.table.showAll')}
                    </Typography>
                    <Chip
                      size="small"
                      label={showOnlySafeEntry ? t('dashboard.sectorRotation.table.filterOnlySafe') : t('dashboard.sectorRotation.table.showAll')}
                      onClick={() => setShowOnlySafeEntry((v) => !v)}
                      color={showOnlySafeEntry ? 'primary' : 'default'}
                      variant={showOnlySafeEntry ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 600, fontSize: '0.875rem', color: '#64748b', py: 1.5 } }}>
                          <TableCell>{t('dashboard.sectorRotation.table.rank')}</TableCell>
                          <TableCell>{t('dashboard.sectorRotation.table.ticker')}</TableCell>
                          <TableCell>{t('dashboard.sectorRotation.table.sector')}</TableCell>
                          <TableCell>{t('dashboard.sectorRotation.table.safeEntry')}</TableCell>
                          <TableCell align="right">{t('dashboard.sectorRotation.table.price')}</TableCell>
                          <TableCell align="right">{t('dashboard.sectorRotation.table.volume')}</TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                              {t('dashboard.sectorRotation.table.rsMom20')}
                              <Tooltip title={t('dashboard.sectorRotation.table.rsMom20Tooltip')} arrow placement="top">
                                <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                              {t('dashboard.sectorRotation.table.turnoverSurge')}
                              <Tooltip title={t('dashboard.sectorRotation.table.turnoverSurgeTooltip')} arrow placement="top">
                                <HelpOutline sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{t('dashboard.sectorRotation.table.setup')}</TableCell>
                          <TableCell align="right">{t('dashboard.sectorRotation.table.score')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedCandidates.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                {showOnlySafeEntry
                                  ? (language === 'vi' ? 'Không có mã nào đạt điểm mua an toàn (anti-chase).' : 'No stocks at safe entry (anti-chase).')
                                  : (language === 'vi' ? 'Chưa có cổ phiếu tiềm năng.' : 'No stock candidates.')}
                              </Typography>
                              {showOnlySafeEntry && (
                                <>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {t('dashboard.sectorRotation.table.noSafeHint')}
                                  </Typography>
                                  {topPicks.length > 0 && (
                                    <Chip
                                      size="small"
                                      label={t('dashboard.sectorRotation.table.showExtendedCount', { count: topPicks.length })}
                                      onClick={() => setShowOnlySafeEntry(false)}
                                      color="primary"
                                      variant="outlined"
                                      sx={{ mt: 2, cursor: 'pointer' }}
                                    />
                                  )}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ) : (
                          displayedCandidates.map((stock) => (
                    <TableRow key={stock.ticker}>
                      <TableCell>{stock.rank_in_sector}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {stock.ticker}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                          {stock.sector_name?.vi || stock.sector_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            size="small"
                            label={stock.passes_safe_entry === true ? t('dashboard.sectorRotation.table.safeEntryLabel') : t('dashboard.sectorRotation.table.extendedLabel')}
                            color={stock.passes_safe_entry === true ? 'success' : 'warning'}
                            variant="outlined"
                          />
                          {stock.safe_entry_detail && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                                {stock.safe_entry_detail.distance_from_ema10_pct != null && `${t('dashboard.sectorRotation.table.detailEma10')}: ${stock.safe_entry_detail.distance_from_ema10_pct >= 0 ? '+' : ''}${Number(stock.safe_entry_detail.distance_from_ema10_pct).toFixed(2)}%`}
                                {stock.safe_entry_detail.distance_from_ema10_pct != null && stock.safe_entry_detail.distance_from_ema20_pct != null && ' · '}
                                {stock.safe_entry_detail.distance_from_ema20_pct != null && `${t('dashboard.sectorRotation.table.detailEma20')}: ${stock.safe_entry_detail.distance_from_ema20_pct >= 0 ? '+' : ''}${Number(stock.safe_entry_detail.distance_from_ema20_pct).toFixed(2)}%`}
                                {(stock.safe_entry_detail.distance_from_ema10_pct != null || stock.safe_entry_detail.distance_from_ema20_pct != null) && stock.safe_entry_detail.atr_multiple != null && ' · '}
                                {stock.safe_entry_detail.atr_multiple != null && `${t('dashboard.sectorRotation.table.detailAtr')}: ${Number(stock.safe_entry_detail.atr_multiple).toFixed(2)}x`}
                              </Typography>
                              {!stock.passes_safe_entry && stock.safe_entry_detail.anti_chase_flags && (stock.safe_entry_detail.anti_chase_flags.too_far_from_ema10 || stock.safe_entry_detail.anti_chase_flags.too_far_from_ema20 || stock.safe_entry_detail.anti_chase_flags.overheated_atr) && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'warning.main' }}>
                                  {[
                                    stock.safe_entry_detail.anti_chase_flags.too_far_from_ema10 && t('dashboard.sectorRotation.table.reasonEma10'),
                                    stock.safe_entry_detail.anti_chase_flags.too_far_from_ema20 && t('dashboard.sectorRotation.table.reasonEma20'),
                                    stock.safe_entry_detail.anti_chase_flags.overheated_atr && t('dashboard.sectorRotation.table.reasonAtr')
                                  ].filter(Boolean).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {!stock.passes_safe_entry && !stock.safe_entry_detail && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                              {t('dashboard.sectorRotation.table.noDetail')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatNumber(stock.close, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatNumber(stock.volume, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2"
                          fontWeight={500}
                          color={stock.rs_mom20_stock >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatPercent(stock.rs_mom20_stock * 100)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatNumber(stock.turnover_surge_stock, 2)}x
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: stock.setup_type === 'MOMENTUM' ? '#3b82f6' : '#8b5cf6',
                            fontWeight: 500,
                            fontSize: '0.8rem'
                          }}
                        >
                          {stock.setup_type}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2"
                          fontWeight={600}
                          sx={{ 
                            color: '#10b981',
                            fontSize: '0.95rem'
                          }}
                        >
                          {formatNumber(stock.stock_score, 1)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()}
          </Paper>
        )}

        {activeTab === 2 && (
          <Box display="flex" gap={2}>
            {/* Active Holdings */}
            <Paper 
              elevation={1} 
              sx={{ 
                flex: 1,
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: 1.5, 
                    bgcolor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUp sx={{ fontSize: 18, color: '#10b981' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#1e293b' }}>
                  {t('dashboard.sectorRotation.activeHoldings')}
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.5,
                  mt: 2
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 1.5,
                    border: '1px solid #f1f5f9'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Inventory2 sx={{ fontSize: 18, color: '#64748b' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b', 
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    >
                      {t('dashboard.sectorRotation.positions')}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={700} 
                    sx={{ color: '#1e293b' }}
                  >
                    {holdings?.active?.total || 0}
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: holdings?.active?.avg_pnl_pct >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: 1.5,
                    border: `1px solid ${holdings?.active?.avg_pnl_pct >= 0 ? '#dcfce7' : '#fee2e2'}`
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShowChart sx={{ fontSize: 18, color: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b', 
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    >
                      {t('dashboard.sectorRotation.performance')}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={700}
                    sx={{ 
                      color: holdings?.active?.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444'
                    }}
                  >
                    {formatPercent(holdings?.active?.avg_pnl_pct || 0)}
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 1.5,
                    border: '1px solid #f1f5f9'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmojiEvents sx={{ fontSize: 18, color: '#10b981' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b', 
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    >
                      {t('dashboard.sectorRotation.winning')}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#10b981' }}>
                    {holdings?.active?.winning || 0}/{holdings?.active?.total || 0}
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 1.5,
                    border: '1px solid #f1f5f9'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule sx={{ fontSize: 18, color: '#64748b' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b', 
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    >
                      {t('dashboard.sectorRotation.avgDays')}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
                    {holdings?.active?.avg_days || 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Closed Holdings */}
            {holdings?.closed?.total > 0 && (
              <Paper 
                elevation={1} 
                sx={{ 
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Box 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: 1.5, 
                      bgcolor: '#e0e7ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 18, color: '#6366f1' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#1e293b' }}>
                    {t('dashboard.sectorRotation.closedHoldings')}
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1.5,
                    mt: 2
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Inventory2 sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b', 
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        {t('dashboard.sectorRotation.positions')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
                      {holdings.closed.total}
                    </Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: holdings.closed.avg_pnl_pct >= 0 ? '#f0fdf4' : '#fef2f2',
                      borderRadius: 1.5,
                      border: `1px solid ${holdings.closed.avg_pnl_pct >= 0 ? '#dcfce7' : '#fee2e2'}`
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <ShowChart sx={{ fontSize: 18, color: holdings.closed.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b', 
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        {t('dashboard.sectorRotation.performance')}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      sx={{ 
                        color: holdings.closed.avg_pnl_pct >= 0 ? '#10b981' : '#ef4444'
                      }}
                    >
                      {formatPercent(holdings.closed.avg_pnl_pct)}
                    </Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #f1f5f9'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b', 
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        {t('dashboard.sectorRotation.avgDays')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
                      {holdings.closed.avg_days}
                    </Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: '#f0fdf4',
                      borderRadius: 1.5,
                      border: '1px solid #dcfce7'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmojiEvents sx={{ fontSize: 18, color: '#10b981' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b', 
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        {t('dashboard.sectorRotation.winRate')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#10b981' }}>
                      {formatPercent(holdings.closed.win_rate)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === 3 && (
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            {alerts.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 600, fontSize: '0.875rem', color: '#64748b', py: 1.5 } }}>
                      <TableCell width="100">{t('dashboard.sectorRotation.table.date')}</TableCell>
                      <TableCell width="120">{t('dashboard.sectorRotation.table.type')}</TableCell>
                      <TableCell width="200">{t('dashboard.sectorRotation.table.sector')}</TableCell>
                      <TableCell>{t('dashboard.sectorRotation.table.message')}</TableCell>
                      <TableCell width="80" align="center">{t('dashboard.sectorRotation.table.severity')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alerts.map((alert, idx) => {
                      const severityColors = {
                        'CRITICAL': { bg: '#fef2f2', text: '#dc2626', icon: '🔴' },
                        'HIGH': { bg: '#fff7ed', text: '#ea580c', icon: '🟠' },
                        'MEDIUM': { bg: '#fefce8', text: '#ca8a04', icon: '🟡' },
                        'LOW': { bg: '#f0fdf4', text: '#16a34a', icon: '🟢' }
                      };
                      const severityStyle = severityColors[alert.severity] || severityColors['LOW'];
                      
                      return (
                        <TableRow 
                          key={idx}
                          sx={{ 
                            '&:hover': { bgcolor: '#f8fafc' },
                            borderLeft: `3px solid ${severityStyle.text}`
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                              {new Date(alert.alert_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                color: alert.alert_type === 'entry' ? '#3b82f6' : '#64748b'
                              }}
                            >
                              {alert.alert_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#1e293b' }}>
                              {alert.sector_name?.[language] || alert.sector_code || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#475569' }}>
                              {typeof alert.message === 'object' && alert.message !== null
                                ? alert.message[language] || alert.message.en || JSON.stringify(alert.message)
                                : alert.message}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: severityStyle.bg,
                                minWidth: 60
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: severityStyle.text,
                                  fontWeight: 600,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {severityStyle.icon}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.sectorRotation.allClear')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('dashboard.sectorRotation.noAlertsAtThisTime')}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default SectorRotation;
